import { Box, Chip, Paper, Stack, Typography, alpha } from "@mui/material";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";

/*
  KeyStructuresTab
  Renders `studyPack.keyStructures`. The shape is either a plain
  string array or an array of objects with `{ name, description,
  location, function }`. We normalise on read.
*/

function normaliseKeyStructure(item, idx) {
  if (typeof item === "string") {
    return { id: `s-${idx}`, name: item };
  }
  return {
    id: `s-${idx}`,
    name: item?.name || item?.title || `Structure ${idx + 1}`,
    description: item?.description,
    location: item?.location,
    function: item?.function,
  };
}

export default function KeyStructuresTab({ studyPack }) {
  const items = (studyPack?.keyStructures || []).map(normaliseKeyStructure);

  if (!items.length) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 3, borderStyle: "dashed" }}>
        <Typography variant="body2" color="text.secondary">
          No key structures were identified in this lecture.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        gap: 1.5,
      }}
    >
      {items.map((s) => (
        <Paper
          key={s.id}
          variant="outlined"
          sx={(t) => ({
            p: 2,
            borderRadius: 3,
            borderColor: t.palette.divider,
            transition: "transform .15s ease, box-shadow .15s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: `0 12px 24px ${alpha(t.palette.primary.main, 0.1)}`,
            },
          })}
        >
          <Stack direction="row" spacing={1.2} alignItems="flex-start">
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={(t) => ({
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: alpha(t.palette.primary.main, 0.12),
                color: t.palette.primary.main,
              })}
            >
              <AccountTreeRoundedIcon fontSize="small" />
            </Stack>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700}>{s.name}</Typography>
              {s.description ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {s.description}
                </Typography>
              ) : null}
              <Stack direction="row" spacing={0.5} mt={1} flexWrap="wrap" sx={{ rowGap: 0.5 }}>
                {s.location ? (
                  <Chip size="small" label={`Location: ${s.location}`} variant="outlined" sx={{ fontSize: 11 }} />
                ) : null}
                {s.function ? (
                  <Chip size="small" label={`Function: ${s.function}`} variant="outlined" sx={{ fontSize: 11 }} />
                ) : null}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      ))}
    </Box>
  );
}
